import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Textarea,
  Button,
  Box,
  Text,
  Tag,
  Select,
  HStack,
  Flex,
} from "@chakra-ui/react";
import {
  dateSecondisBeforeDateFirst,
  currentTime,
  normalizeEtaTimestamp,
} from "../../../utils/dateResolvers";
import {
  isReservedSystemEtaReason,
  RESERVED_SYSTEM_ETA_REASON_MESSAGE,
} from "../../../utils/orderEtaReason";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { AllocatedTo } from "./GaraazOrderComponent";
import { useGetAllSuppliers } from "../queryHooks";
import { useUserDetailsContext } from "../../../context/userDetailsContext";
import { isUserInDevsUserIdsConfig } from "../utils/deleteOrderUtils";
import {
  etaFromDateAndSlot,
  findEarliestDateWithSelectableSlots,
  GaraazSlotPickerWindow,
  IST_TIMEZONE,
  orderEtaSlotFieldsFromDateAndSlot,
  OrderEtaSlotPayload,
  slotsSelectableOnDate,
} from "../utils/garaazSlotPickerUtils";
import { formatClockTimeAmPm } from "../../../components/DeliveryOptions/utils/deliveryTimeUtils";

dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_ENQUIRY_ETA_MINUTES = 30;
const DEFAULT_BUSINESS_HOURS = { start: "09:30", end: "19:00" } as const;
const USER_CONFIRMATION_PHRASE = "I have informed the customer";
const HH_MM_PATTERN = /^([01]?\d|2[0-3]):([0-5]\d)$/;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  orderETAs: any[];
  modifiedOrderETAs: any[];
  handleSubmit: (
    etaGivenTime: string,
    eta: string,
    reason: string,
    TobeAllocateSupplierId?: string,
    handleModalCLose?: () => void,
    slotPayload?: OrderEtaSlotPayload | null,
  ) => void | Promise<void>;
  modelHeader: string;
  okBtnText?: string;
  isCBOETAPicker?: boolean;
  allAlocatedSuppliers?: AllocatedTo[];
  activeSupplierId?: string;
  isEnquiryOrder?: boolean;
  isEnquiryResolutionPending?: boolean;
  useGaraazSlots?: boolean;
  garaazSlots?: GaraazSlotPickerWindow[];
};

type BusinessHours = { start: string; end: string };

function normalizeConfirmationText(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function readEnquiryEtaMinutes(configData: { enquiry_order_auto_eta_minutes?: unknown } | undefined): number {
  const raw = configData?.enquiry_order_auto_eta_minutes;
  const parsed = typeof raw === "number" ? raw : parseInt(String(raw ?? ""), 10);
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return DEFAULT_ENQUIRY_ETA_MINUTES;
}

function parseHhMm(value: string | undefined): { hours: number; minutes: number } | null {
  if (!value || typeof value !== "string") {
    return null;
  }
  const match = HH_MM_PATTERN.exec(value.trim());
  if (!match) {
    return null;
  }
  return {
    hours: parseInt(match[1], 10),
    minutes: parseInt(match[2], 10),
  };
}

function readBusinessHours(
  configData: { garaaz_business_hours?: unknown } | undefined,
): BusinessHours {
  const raw = configData?.garaaz_business_hours;
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { ...DEFAULT_BUSINESS_HOURS };
    }
  }
  if (
    parsed &&
    typeof parsed === "object" &&
    typeof (parsed as BusinessHours).start === "string" &&
    typeof (parsed as BusinessHours).end === "string" &&
    parseHhMm((parsed as BusinessHours).start) &&
    parseHhMm((parsed as BusinessHours).end)
  ) {
    return {
      start: (parsed as BusinessHours).start.trim(),
      end: (parsed as BusinessHours).end.trim(),
    };
  }
  return { ...DEFAULT_BUSINESS_HOURS };
}

export function isEnquiryRootOrder(order?: {
  _id?: unknown;
  rootOrderId?: unknown;
} | null): boolean {
  if (order?._id == null || order?.rootOrderId == null) {
    return false;
  }
  return (
    String(order._id) === String(order.rootOrderId) 
  );
}

/**
 * Enquiry auto-ETA in IST wall-clock.
 * If now + minutes falls after today's business end, roll to
 * next day's business start + the same minutes.
 */
export function computeEnquiryEtaWithBusinessHours(
  minutes: number,
  businessHours: BusinessHours = DEFAULT_BUSINESS_HOURS,
  now: dayjs.Dayjs = dayjs(),
): string {
  const nowIst = now.tz(IST_TIMEZONE);
  const endParts = parseHhMm(businessHours.end) ?? parseHhMm(DEFAULT_BUSINESS_HOURS.end)!;
  const startParts =
    parseHhMm(businessHours.start) ?? parseHhMm(DEFAULT_BUSINESS_HOURS.start)!;

  const todayEnd = nowIst
    .hour(endParts.hours)
    .minute(endParts.minutes)
    .second(0)
    .millisecond(0);

  const tentativeEta = nowIst.add(minutes, "minute");

  if (tentativeEta.isAfter(todayEnd)) {
    return nowIst
      .add(1, "day")
      .hour(startParts.hours)
      .minute(startParts.minutes)
      .second(0)
      .millisecond(0)
      .add(minutes, "minute")
      .format("YYYY-MM-DDTHH:mm:ss");
  }

  return tentativeEta.format("YYYY-MM-DDTHH:mm:ss");
}

const GaraazDatePickerModalETA = (props: Props) => {
  const [etaGivenTime, setEtaGivenTime] = useState(
    currentTime
  );
  const [eta, setEta] = useState(`${dayjs(new Date()).format("YYYY-MM-DD")}T`);

  const { data: allSuppliers } = useGetAllSuppliers();
  const { data: configData } = useGetOrderMediaConfig();
  const userDetails = useUserDetailsContext();
  const canPasteConfirmation = isUserInDevsUserIdsConfig(
    userDetails?._id,
    configData?.devs_user_ids,
  );

  const [selectAllocateSupplier, setSelectAllocateSupplier] = useState(
    props.activeSupplierId || ""
  );

  const [reason, setReason] = useState("");
  const [customerConfirmation, setCustomerConfirmation] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedQuickEta, setSelectedQuickEta] = useState("");
  const [selectedGaraazSlotId, setSelectedGaraazSlotId] = useState<
    number | null
  >(null);
  /** Bumps whenever the ETA modal opens so today's slots re-filter with a fresh clock. */
  const [slotFilterOpenKey, setSlotFilterOpenKey] = useState(0);

  const currentDateTime = new Date().toISOString().slice(0, 16);

  const getSupplierBusinessName = useCallback(
    (supplierId: string) => {
      const supplier = allSuppliers?.find(
        (supplier: any) => supplier._id === supplierId
      );
      return supplier ? supplier.businessName : "";
    },
    [allSuppliers]
  );

  const enquiryEtaMinutes = readEnquiryEtaMinutes(
    configData as unknown as { enquiry_order_auto_eta_minutes?: unknown },
  );
  const businessHours = readBusinessHours(
    configData as unknown as { garaaz_business_hours?: unknown },
  );
  const isEnquiryFixedEta = Boolean(props.isEnquiryOrder) && !props.isCBOETAPicker;
  const isGaraazSlotPicker =
    Boolean(props.useGaraazSlots) &&
    !props.isCBOETAPicker &&
    !isEnquiryFixedEta &&
    !props.isEnquiryResolutionPending;
  const isConfirmationMatch =
    normalizeConfirmationText(customerConfirmation) ===
    normalizeConfirmationText(USER_CONFIRMATION_PHRASE);

  const resolveEnquiryEta = useCallback(
    () => computeEnquiryEtaWithBusinessHours(enquiryEtaMinutes, businessHours),
    [enquiryEtaMinutes, businessHours.start, businessHours.end],
  );

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }
    const now = dayjs();
    setSlotFilterOpenKey((key) => key + 1);
    setEtaGivenTime(currentTime);
    const useSlotPickerOnOpen =
      Boolean(props.useGaraazSlots) &&
      !props.isCBOETAPicker &&
      !isEnquiryFixedEta;
    let nextEtaDate = now.tz(IST_TIMEZONE).format("YYYY-MM-DD");
    if (useSlotPickerOnOpen) {
      nextEtaDate =
        findEarliestDateWithSelectableSlots(props.garaazSlots ?? [], now) ??
        nextEtaDate;
    }
    setEta(
      isEnquiryFixedEta ? resolveEnquiryEta() : `${nextEtaDate}T`,
    );
    setReason("");
    setCustomerConfirmation("");
    setSelectedQuickEta("");
    setSelectedGaraazSlotId(null);
    setIsSubmitting(false);
    setSelectAllocateSupplier(props.activeSupplierId || "");
  }, [props.isOpen]);

  useEffect(() => {
    if (!props.isOpen || !isEnquiryFixedEta) {
      return;
    }
    const tick = () => {
      setEta(resolveEnquiryEta());
    };
    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [props.isOpen, isEnquiryFixedEta, resolveEnquiryEta]);

  function handleCloseModal() {
    props.onClose();
    setEtaGivenTime(currentTime);
    setEta("");
    setReason("");
    setCustomerConfirmation("");
    setSelectedQuickEta("");
    setSelectedGaraazSlotId(null);
    setIsSubmitting(false);
    setSelectAllocateSupplier(props?.activeSupplierId || "");
  }

  const handleFormSubmit = async () => {
    if (isSubmitting) {
      return;
    }
    if (isReservedSystemEtaReason(reason) || !isConfirmationMatch) {
      return;
    }
    setIsSubmitting(true);
    try {
      const liveEta = isEnquiryFixedEta ? resolveEnquiryEta() : eta;
      const normalizedGivenTime = normalizeEtaTimestamp(etaGivenTime);
      const normalizedEta = normalizeEtaTimestamp(liveEta);
      const dateForSlot = eta.slice(0, 10);
      const selectedSlot =
        isGaraazSlotPicker && selectedGaraazSlotId != null
          ? (props.garaazSlots ?? []).find(
              (slot) => slot.slotId === selectedGaraazSlotId,
            )
          : undefined;
      const slotPayload =
        selectedSlot && dateForSlot.length === 10
          ? orderEtaSlotFieldsFromDateAndSlot(dateForSlot, selectedSlot)
          : null;
      if (props.isCBOETAPicker) {
        await props.handleSubmit(
          normalizedGivenTime,
          normalizedEta,
          reason,
          selectAllocateSupplier,
          props.onClose,
        );
      } else {
        await props.handleSubmit(
          normalizedGivenTime,
          normalizedEta,
          reason,
          undefined,
          undefined,
          slotPayload,
        );
      }
      handleCloseModal();
    } catch {
      // Keep picker open when submit rejects (caller surfaces the error)
      setIsSubmitting(false);
    }
  };

  const isReasonValid =
    reason.length >= Number(JSON.parse(configData?.min_reason_length) ?? "");
  const isSystemReason = isReservedSystemEtaReason(reason);
  const isSecondEntry = props.orderETAs?.length > 0;
  const isSaveButtonEnabled =
    isConfirmationMatch &&
    (isSecondEntry ? isReasonValid && !isSystemReason : !isSystemReason);

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
  };

  function handleETATimeChange(e: any, type: string) {
    setSelectedQuickEta("");
    if (type === "Date") {
      e += "T";
    } else {
      if (eta.length < 11) {
        alert("Please select date first");
      } else {
        e = eta.slice(0, 11) + e;
      }
    }
    const res = dateSecondisBeforeDateFirst(etaGivenTime, e);
    if (res) {
      alert("ETA time can not be less than ETA given time");
    } else {
      setEta(e);
    }
  }

  function minutesToHours(minutes: number) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 0) return `${remainingMinutes} mins`;
    if (remainingMinutes > 0) return `${hours}:${remainingMinutes} hrs`;
    return `${hours} hrs`;
  }

  function addTimeRangeToCurrentTime(
    minutesToAdd: number,
    timeUnit: "second" | "minute" | "day"
  ) {
    const now = dayjs().tz(IST_TIMEZONE);
    const futureTime = now.add(minutesToAdd, timeUnit);
    return futureTime.format("YYYY-MM-DDTHH:mm:ss");
  }

  const etaTimeInputValue =
    eta.length >= 16 ? eta.slice(11, 16) : eta.slice(11);
  const selectedDateIst = eta.slice(0, 10);
  const earliestSelectableSlotDate = useMemo(() => {
    if (!isGaraazSlotPicker) {
      return dayjs().tz(IST_TIMEZONE).format("YYYY-MM-DD");
    }
    return (
      findEarliestDateWithSelectableSlots(props.garaazSlots ?? [], dayjs()) ??
      dayjs().tz(IST_TIMEZONE).format("YYYY-MM-DD")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGaraazSlotPicker, props.garaazSlots, slotFilterOpenKey]);

  const selectableGaraazSlots = useMemo(
    () =>
      isGaraazSlotPicker
        ? slotsSelectableOnDate(
            props.garaazSlots ?? [],
            selectedDateIst,
            dayjs(),
          )
        : [],
    // slotFilterOpenKey forces a fresh "now" each time the modal opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isGaraazSlotPicker,
      props.garaazSlots,
      selectedDateIst,
      slotFilterOpenKey,
    ],
  );

  useEffect(() => {
    if (!isGaraazSlotPicker || !props.isOpen) {
      return;
    }
    if (selectedDateIst.length !== 10) {
      return;
    }
    // Selected date has no remaining slots → jump to earliest allowed date.
    if (
      selectableGaraazSlots.length === 0 &&
      earliestSelectableSlotDate &&
      selectedDateIst !== earliestSelectableSlotDate
    ) {
      setSelectedGaraazSlotId(null);
      setEta(`${earliestSelectableSlotDate}T`);
      return;
    }
    if (selectedGaraazSlotId == null) {
      return;
    }
    const stillSelectable = selectableGaraazSlots.some(
      (slot) => slot.slotId === selectedGaraazSlotId,
    );
    if (!stillSelectable) {
      setSelectedGaraazSlotId(null);
      setEta(`${selectedDateIst}T`);
    }
  }, [
    isGaraazSlotPicker,
    props.isOpen,
    selectableGaraazSlots,
    selectedGaraazSlotId,
    selectedDateIst,
    earliestSelectableSlotDate,
  ]);

  function handleGaraazSlotDateChange(dateValue: string) {
    setSelectedGaraazSlotId(null);
    if (!dateValue) {
      setEta("");
      return;
    }
    // Block dates before the earliest day that still has open slots.
    const nextDate =
      dateValue < earliestSelectableSlotDate
        ? earliestSelectableSlotDate
        : dateValue;
    setEta(`${nextDate}T`);
  }

  function handleGaraazSlotSelect(slot: GaraazSlotPickerWindow) {
    if (selectedDateIst.length < 10) {
      return;
    }
    const nextEta = etaFromDateAndSlot(selectedDateIst, slot);
    if (!nextEta) {
      return;
    }
    const res = dateSecondisBeforeDateFirst(etaGivenTime, nextEta);
    if (res) {
      alert("ETA time can not be less than ETA given time");
      return;
    }
    setSelectedGaraazSlotId(slot.slotId);
    setEta(nextEta);
  }

  return (
    <Modal
      returnFocusOnClose={false}
      isOpen={props.isOpen}
      onClose={props.onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isGaraazSlotPicker ? "Please provide new CPT" : props.modelHeader}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {props.isCBOETAPicker && (
            <FormControl mb={4} width={"100%"}>
              <FormLabel>Select Supplier</FormLabel>
              <Select
                placeholder="Select Supplier"
                defaultValue={props?.activeSupplierId}
                onChange={(e) => {
                  const supplierId = e.target.value;
                  setSelectAllocateSupplier(supplierId);
                }}
              >
                {props?.allAlocatedSuppliers?.map((supplierInfo, index) => (
                  <option key={index} value={supplierInfo?.supplierId}>
                    {getSupplierBusinessName(supplierInfo?.supplierId)}
                  </option>
                ))}
              </Select>
              {!selectAllocateSupplier && (
                <Text colorScheme="red" color="red">
                  Please allocate supplier first.
                </Text>
              )}
            </FormControl>
          )}
          <FormControl width="100%">
            {!isGaraazSlotPicker ? <FormLabel>New ETA</FormLabel> : null}
            {props.isEnquiryResolutionPending ? (
              <Text fontSize="sm" color="gray.600">
                Checking whether this ETA is fixed…
              </Text>
            ) : isEnquiryFixedEta ? (
              <Box>
                <Input
                  isReadOnly
                  value={
                    dayjs(eta).isValid()
                      ? dayjs(eta).format("DD MMM YYYY, hh:mm A")
                      : ""
                  }
                />
                <Text mt={1} fontSize="2xs" color="gray.600">
                  Estimate ETA is fixed at {enquiryEtaMinutes} minutes from now
                  (business hours {businessHours.start}–{businessHours.end} IST).
                  If that falls after closing, it rolls to next day{" "}
                  {businessHours.start} + {enquiryEtaMinutes} min.
                </Text>
              </Box>
            ) : isGaraazSlotPicker ? (
              <Box>
                <Box mb={3} width={"50%"}>
                  <Text mb={1}>When</Text>
                  <Input
                    type="date"
                    value={selectedDateIst}
                    onChange={(e) => handleGaraazSlotDateChange(e.target.value)}
                    min={earliestSelectableSlotDate}
                  />
                </Box>
                <Text mb={1}>Slot</Text>
                {selectableGaraazSlots.length === 0 ? (
                  <Text fontSize="sm" color="gray.600">
                    {selectedDateIst.length === 10
                      ? "No Garaaz slots are configured for delivery."
                      : "Select a date to see Garaaz slots."}
                  </Text>
                ) : (
                  <Flex wrap="wrap" gap={2}>
                    {selectableGaraazSlots.map((slot) => (
                      <Tag
                        key={slot.slotId}
                        cursor="pointer"
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-start"
                        py={2}
                        px={3}
                        height="auto"
                        minH="auto"
                        backgroundColor={
                          selectedGaraazSlotId === slot.slotId
                            ? "#fb8f67"
                            : "#EDF2F6"
                        }
                        onClick={() => handleGaraazSlotSelect(slot)}
                        title={`${formatClockTimeAmPm(slot.startTime)} – ${formatClockTimeAmPm(slot.endTime)}`}
                      >
                        <Text fontSize="sm" fontWeight="semibold">
                          Slot {slot.slotId}
                        </Text>
                        <Text fontSize="2xs" opacity={0.85}>
                          {formatClockTimeAmPm(slot.startTime)} –{" "}
                          {formatClockTimeAmPm(slot.endTime)}
                        </Text>
                      </Tag>
                    ))}
                  </Flex>
                )}
              </Box>
            ) : (
              <>
                <Box mb={2}>
                  {props.modifiedOrderETAs?.map((item: any, index: number) => {
                    return (
                      <Tag
                        mr={2}
                        key={index}
                        cursor={"pointer"}
                        backgroundColor={
                          selectedQuickEta === item ? "#fb8f67" : "#EDF2F6"
                        }
                        onClick={() => {
                          if (eta.length < 11) {
                            alert("Please select date first");
                          } else {
                            if (selectedQuickEta === item) {
                              setSelectedQuickEta("");
                              let newEtaValue = eta.slice(0, 11);
                              setEta(newEtaValue);
                            } else {
                              let newEtaValue;
                              if (props.isCBOETAPicker) {
                                newEtaValue = addTimeRangeToCurrentTime(
                                  item,
                                  "day",
                                );
                              } else {
                                newEtaValue = addTimeRangeToCurrentTime(
                                  item,
                                  "minute",
                                );
                              }
                              const res = dateSecondisBeforeDateFirst(
                                etaGivenTime,
                                newEtaValue,
                              );
                              if (res) {
                                alert(
                                  "ETA time can not be less than ETA given time",
                                );
                              } else {
                                setSelectedQuickEta(item);
                                setEta(newEtaValue);
                              }
                            }
                          }
                        }}
                      >
                        {props.isCBOETAPicker
                          ? `${item} days`
                          : minutesToHours(item)}
                      </Tag>
                    );
                  })}
                </Box>
                <Box display={"flex"}>
                  <Box width={"50%"}>
                    <Text mb={1}>When</Text>
                    <Input
                      type="date"
                      value={eta.slice(0, 10)}
                      onChange={(e) =>
                        handleETATimeChange(e.target.value, "Date")
                      }
                      min={currentDateTime}
                    />
                  </Box>
                  <Box ml={4} width={"50%"}>
                    <Text mb={1}>Time</Text>
                    <Input
                      type="time"
                      value={etaTimeInputValue}
                      onChange={(e) =>
                        handleETATimeChange(e.target.value, "Time")
                      }
                      min={currentDateTime}
                    />
                  </Box>
                </Box>
              </>
            )}
          </FormControl>
          {isSecondEntry && (
            <FormControl mt={4} width="100%" isInvalid={isSystemReason}>
              <FormLabel>
                {isGaraazSlotPicker ? "Reason" : "Reason for ETA Changed"}
              </FormLabel>
              <Textarea
                minLength={50}
                value={reason}
                onChange={handleReasonChange}
                placeholder={
                  isGaraazSlotPicker
                    ? "Why was the CPT updated?"
                    : "Why was the ETA updated?"
                }
              />
              {isSystemReason ? (
                <FormErrorMessage>
                  {RESERVED_SYSTEM_ETA_REASON_MESSAGE}
                </FormErrorMessage>
              ) : null}
            </FormControl>
          )}
          <FormControl
            mt={4}
            width="100%"
            isInvalid={
              customerConfirmation.trim().length > 0 && !isConfirmationMatch
            }
          >
            <FormHelperText mb={1}>
              Type{" "}
              <span style={{ fontWeight: "bold", userSelect: canPasteConfirmation ? "text" : "none" }}>
                "{USER_CONFIRMATION_PHRASE}"
              </span> to continue.
            </FormHelperText>
            <Input
              value={customerConfirmation}
              onChange={(e) => setCustomerConfirmation(e.target.value)}
              onPaste={(e) => {
                if (!canPasteConfirmation) {
                  e.preventDefault();
                }
              }}
              placeholder={USER_CONFIRMATION_PHRASE}
              autoComplete="off"
            />
            <FormErrorMessage>
              Confirmation must match the phrase above.
            </FormErrorMessage>
          </FormControl>
        </ModalBody>
        <ModalFooter mt={-2}>
          <HStack justifyContent="end" spacing={3} w="full">
            <Button
              variant="outline"
              borderColor="gray.300"
              onClick={handleCloseModal}
              h="44px"
              borderRadius="lg"
            >
              Cancel
            </Button>
            <Button
              isDisabled={
                isSubmitting ||
                Boolean(props.isEnquiryResolutionPending) ||
                !isSaveButtonEnabled ||
                eta.length < 16 ||
                etaGivenTime.length < 16
              }
              isLoading={isSubmitting}
              colorScheme="blue"
              onClick={handleFormSubmit}
              h="44px"
              borderRadius="lg"
            >
              {props.okBtnText || "Confirm"}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GaraazDatePickerModalETA;
